// src/lib/goalInput.js — pure goal-data helpers (distance labels, target
// time + race-date input). Sense-check 2026-09-19 Tier 3 (C3, C7, C8).
//
// The goal's DISTANCE (km) and TARGET (seconds, or null = "just finish") are
// the source of truth end to end. Labels ("50M", "Custom") are display only —
// parsing them back to km lost the distance (50M → 50 km, 100M → 100 km,
// Custom → 42.195).
//
// CommonJS + no object spread (CRA consumes it via module.exports; same
// constraint as the generator mirror, CLAUDE.md §"Generator mirror").

const MILE_50_KM = 80.467;
const MILE_100_KM = 160.934;

/** Display label for a goal distance. Labels match the goal chips. */
function goalTypeFromKm(rawKm) {
  const km = Number(rawKm) || 0;
  if (km === 5) return '5K';
  if (km === 10) return '10K';
  if (Math.abs(km - 21.0975) < 0.5) return 'Half Marathon';
  if (Math.abs(km - 42.195) < 0.5) return 'Marathon';
  if (km === 50) return '50K';
  if (Math.abs(km - 80) < 1) return '50M';
  if (km === 100) return '100K';
  if (Math.abs(km - 161) < 2) return '100M';
  return 'Custom';
}

/**
 * km for a goal label — LEGACY fallback only (old localStorage state without
 * a stored distanceKm). Returns null when the label carries no distance
 * ("Custom") instead of inventing a marathon.
 */
function distanceFromGoalType(goalType) {
  const g = String(goalType || '').trim().toLowerCase();
  if (!g) return null;
  if (g === '100m' || g.includes('100 mi') || g.includes('100-mile')) return MILE_100_KM;
  if (g === '50m' || g.includes('50 mi') || g.includes('50-mile')) return MILE_50_KM;
  if (g.includes('half')) return 21.0975;
  if (g.includes('marathon')) return 42.195;
  if (g.includes('100k')) return 100;
  if (g.includes('50k')) return 50;
  if (g.includes('10k')) return 10;
  if (g.includes('5k')) return 5;
  const m = g.match(/(\d+(\.\d+)?)\s*k/);
  return m ? Number(m[1]) : null;
}

/** Goal distance: a stored km always wins; the label is a legacy fallback. */
function resolveGoalDistanceKm(goalSaved, fallbackType) {
  const km = Number(goalSaved && goalSaved.distanceKm);
  if (Number.isFinite(km) && km > 0) return km;
  return distanceFromGoalType((goalSaved && goalSaved.type) || fallbackType);
}

/**
 * Target time typed by the athlete in the Adjust-goal sheet ("h:mm" or
 * "h:mm:ss" — hours first, as the field label says). Returns seconds, null
 * for an empty field (= no time goal), or NaN for invalid input.
 */
function parseAdjustTime(str) {
  const t = String(str == null ? '' : str).trim();
  if (!t) return null;
  if (!/^\d{1,3}:\d{2}(:\d{2})?$/.test(t)) return NaN;
  const p = t.split(':').map(Number);
  if (p[1] > 59 || (p.length === 3 && p[2] > 59)) return NaN;
  const sec = p[0] * 3600 + p[1] * 60 + (p.length === 3 ? p[2] : 0);
  return sec > 0 ? sec : NaN;
}

/** Seconds → "H:MM:SS" (three parts, unambiguous for every parser). */
function formatHms(sec) {
  const t = Math.round(Number(sec));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return h + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
}

/**
 * Validate the Adjust-goal sheet. `todayIso` = local 'YYYY-MM-DD'.
 * → { ok:true, raceDate, targetFinishSec, targetFinish, dateChanged }
 * → { ok:false, error }
 */
function validateAdjustGoal(input) {
  const i = input || {};
  const date = String(i.date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return { ok: false, error: 'Pick a race date.' };
  const d = new Date(date + 'T00:00:00');
  if (isNaN(+d) || (d.getMonth() + 1) !== Number(date.slice(5, 7))) return { ok: false, error: 'That date does not exist.' };
  if (i.todayIso && date < i.todayIso) return { ok: false, error: 'The race date is in the past.' };
  const sec = parseAdjustTime(i.time);
  if (Number.isNaN(sec)) return { ok: false, error: 'Enter the goal time as h:mm or h:mm:ss (e.g. 12:30).' };
  return {
    ok: true,
    raceDate: date,
    targetFinishSec: sec,
    targetFinish: sec != null ? formatHms(sec) : null,
    dateChanged: date !== (i.currentDate || ''),
  };
}

module.exports = {
  MILE_50_KM,
  MILE_100_KM,
  goalTypeFromKm,
  distanceFromGoalType,
  resolveGoalDistanceKm,
  parseAdjustTime,
  formatHms,
  validateAdjustGoal,
};
